import { SlideThumbnailPanel } from '@/components/slides/SlideThumbnailPanel/SlideThumbnailPanel';
import { useClassroom } from '@/hooks/useClassroom/useClassroom';
import { useClassroomSession } from '@/hooks/useClassroomSession/useClassroomSession';
import { Classroom } from '@/types/classroom';
import { parseSlide, Slide as SlideData } from '@/types/slide';
import { useLazyQuery, useQuery } from '@apollo/client';
import { Box, Skeleton } from '@mui/material';
import { useNotifications } from '@toolpad/core';
import React, { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate, useParams } from 'react-router';
import { Slide } from '../../components/slides/Slide';
import { ClassroomSettings, ClassroomState } from '../../types/session';
import { ClassroomAssignStudentDialog } from './components/ClassroomAssignStudentDialog';
import { ClassroomPageHeader } from './components/ClassroomPageHeader';
import { ClassroomScreenControl } from './components/ClassroomScreenControl';
import { GET_CLASSROOM, GET_LESSON_VERSION, GetClassResponse, GetLessonVersionResponse, GetLessonVersionVariables } from './gql';

import { DisconnectedDialog } from '@/components/dialogs/DisconnectedDialog';
import { SlideContainer } from '@/components/slides/SlideContainer';
import { capture } from '@/handlers/error';
import { useMedia } from '@/hooks/useMedia/useMedia';
import { useStudentResults } from '@/hooks/useStudentResults/useStudentResults';
import { EditorProvider } from '@/providers/editor/EditorProvider';
import { useKeyBindings } from 'rooks';

function hasNextSlide(settings: ClassroomSettings, slides: SlideData[]) {
    return settings.page < slides.length - 1;
}

function hasPreviousSlide(settings: ClassroomSettings) {
    return settings.page > 0;
}

export default function ClassroomPage() {
    const params = useParams();
    const classroomId = params.classroomId;
    if (!classroomId) {
        // required params
        throw new Error('Classroom ID is required');
    }

    // providers
    const { t } = useTranslation();
    const navigate = useNavigate();
    const notifications = useNotifications();
    const { isMobile } = useMedia();

    // gql
    const { data } = useQuery<GetClassResponse>(GET_CLASSROOM, { variables: { where: { id: classroomId } } });
    const [queryLessonVersion] = useLazyQuery<GetLessonVersionResponse, GetLessonVersionVariables>(GET_LESSON_VERSION);

    // ui state
    const [slides, setSlides] = React.useState<SlideData[] | undefined>();
    const [showAssignStudentDialog, setShowAssignStudentDialog] = React.useState(false);

    // ref
    const slideContainerRef = React.useRef<HTMLDivElement>(null);

    // classroom session hooks
    const { terminate: terminateClassroom } = useClassroom();
    const {
        state: roomState,
        clients,
        students,
        settings,
        setSettings,
        start: startClassroomSession,
        terminate: terminateClassroomSession,
        allocate,
        deallocate,
        reallocate,
        connectionStatus,
    } = useClassroomSession(data?.testroom);
    const { results, refetch } = useStudentResults({ type: 'classroom', id: classroomId });

    /**
     * handlers
     */

    const handleEndClass = React.useCallback(
        async (classroom: Classroom) => {
            try {
                await terminateClassroomSession();
                await terminateClassroom(classroom);
            } catch (error) {
                notifications.show(error instanceof Error ? error.message : `${error}`, { severity: 'error', autoHideDuration: 5000 });
                capture(error);
            } finally {
                if (window.history?.length && window.history.length > 1) {
                    navigate(-1);
                } else {
                    navigate('/', { replace: true });
                }
            }
        },
        [navigate, notifications, terminateClassroom, terminateClassroomSession],
    );

    const handleSettingsChange = React.useCallback(
        async (settings: ClassroomSettings) => {
            await setSettings(settings);
        },
        [setSettings],
    );

    const handleReassignStudents = React.useCallback(async () => {
        reallocate();
    }, [reallocate]);

    const handleAutoAllocate = React.useCallback(async () => {
        const availableStudents = students
            ?.filter((student) => clients?.findIndex((client) => student.id === client.studentProfileId) === -1)
            .sort((a, b) => (results[a.id]?.responseCount ?? 0) - (results[b.id]?.responseCount ?? 0));
        const availableDevices = clients?.filter((client) => !client.studentProfileId);

        const labels = [];
        for (const availableDevice of availableDevices) {
            const student = availableStudents?.shift();
            if (student) {
                labels.push({ profile: student, clientId: availableDevice.socketId });
            }
        }

        await allocate(labels);
    }, [allocate, clients, students, results]);

    const handleDeallocateAll = React.useCallback(async () => {
        await deallocate(clients.map((client) => client.socketId));
    }, [clients, deallocate]);

    const handleFullScreen = React.useCallback(() => {
        if (slideContainerRef.current) {
            slideContainerRef.current.requestFullscreen().catch((err) => {
                notifications.show(err instanceof Error ? err.message : `${err}`, { severity: 'error', autoHideDuration: 5000 });
            });
        }
    }, [notifications]);

    // keybind
    useKeyBindings({
        ArrowLeft: () => {
            if (settings && hasPreviousSlide(settings)) {
                setSettings({
                    ...settings,
                    page: settings.page - 1,
                });
            }
        },
        ArrowRight: () => {
            if (settings && slides && hasNextSlide(settings, slides)) {
                setSettings({
                    ...settings,
                    page: settings.page + 1,
                });
            }
        },
        f: handleFullScreen,
    });

    /**
     * effects
     */

    useEffect(() => {
        if (roomState === ClassroomState.ENDED) {
            notifications.show(t('classroom.error.ended'), { severity: 'error', autoHideDuration: 5000 });
            navigate('/lessons', { replace: true });
        } else if (roomState === ClassroomState.ASSIGNING) {
            refetch();
        }

        setShowAssignStudentDialog(roomState === ClassroomState.ASSIGNING);
    }, [refetch, roomState, notifications, navigate, t]);

    useEffect(() => {
        if (data?.testroom?.lessonVersion.id) {
            queryLessonVersion({ variables: { where: { id: data.testroom.lessonVersion.id } } }).then((respond) => {
                setSlides(() => {
                    const slides = respond.data?.lessonVersion.slides.map((slide) => parseSlide(slide));
                    slides?.sort((a, b) => a.sort - b.sort);
                    return slides;
                });
            });
        }
    }, [data, queryLessonVersion]);

    return (
        <div style={{ width: '100vw', height: '100svh', display: 'flex', flexDirection: 'column' }}>
            <ClassroomPageHeader classroom={data?.testroom} settings={settings} onEndClass={handleEndClass} onSettingsChange={handleSettingsChange} onReassignStudents={handleReassignStudents} />
            <EditorProvider>
                <Box component='div' sx={{ backgroundColor: 'black', flex: 1, display: 'flex', flexDirection: 'column' }} data-mui-color-scheme='dark'>
                    <ClassroomScreenControl
                        settings={settings}
                        onScreenSync={async (checked) => {
                            if (settings) {
                                await setSettings({
                                    ...settings,
                                    screenSync: checked,
                                });
                            }
                        }}
                        onFullScreen={handleFullScreen}
                    />
                    <div ref={slideContainerRef} style={{ flex: 1, display: 'flex', flexDirection: 'column', margin: '32px 16px', overflow: 'hidden', overflowX: isMobile ? 'scroll' : 'clip' }}>
                        <SlideContainer
                            padding={0}
                            onPreviousSlide={
                                settings && hasPreviousSlide(settings)
                                    ? () => {
                                          setSettings({
                                              ...settings,
                                              page: settings.page - 1,
                                          });
                                      }
                                    : undefined
                            }
                            onNextSlide={
                                settings && slides && hasNextSlide(settings, slides)
                                    ? () => {
                                          setSettings({
                                              ...settings,
                                              page: settings.page + 1,
                                          });
                                      }
                                    : undefined
                            }
                        >
                            {settings && slides ? <Slide components={slides[settings.page]?.data ?? []} preview interactive /> : <Skeleton variant='rounded' sx={{ height: '100%', width: '100%' }} />}
                        </SlideContainer>
                    </div>
                    <SlideThumbnailPanel
                        slides={slides}
                        thumbnailIdx={settings?.page}
                        onThumbnailChange={async (index) => {
                            if (settings) {
                                await setSettings({
                                    ...settings,
                                    page: index,
                                });
                            }
                        }}
                    />
                </Box>
            </EditorProvider>
            {showAssignStudentDialog && (
                <ClassroomAssignStudentDialog
                    open={showAssignStudentDialog}
                    onClose={async () => {
                        startClassroomSession();
                    }}
                    payload={{
                        otp: data?.testroom?.otp,
                        clients: clients,
                        students: students,
                        onAllocate: async (profile, clientId) => {
                            await allocate([{ profile, clientId }]);
                        },
                        onDeallocate: async (clientId) => {
                            await deallocate([clientId]);
                        },
                        onAutoAllocate: handleAutoAllocate,
                        onDeallocateAll: handleDeallocateAll,
                        results: results,
                    }}
                />
            )}
            <DisconnectedDialog open={connectionStatus !== 'connected'} connectionStatus={connectionStatus} />
        </div>
    );
}
